import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileArchive, Download, ListChecks, FileText, Sparkles, AlertTriangle, Lock, PenLine } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const CK_STATUS = ["not_started", "in_progress", "complete", "not_applicable"];

export default function Submissions() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data);
      if (data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("Load projects:", e?.response?.status));
  }, [authLoading]);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><FileArchive className="w-3 h-3" /> Submissions</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            510(k) Submission Bundle
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Draft-scaffold 21 CFR 807 sections. VitaQMS assembles a ZIP + PDF bundle from your device data.
            A qualified regulatory reviewer must complete every section before the eSTAR submission — this tool
            does <b>not</b> submit to the FDA and does not guarantee clearance.
          </p>
        </div>

        <div className="mb-6 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="sub-project-select" className="mt-1">
              <SelectValue placeholder="Select project…" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {pid && (
          <Tabs defaultValue="checklist">
            <TabsList data-testid="sub-tabs">
              <TabsTrigger value="checklist" data-testid="tab-checklist"><ListChecks className="w-4 h-4 mr-1" /> 510(k) Checklist</TabsTrigger>
              <TabsTrigger value="bundles" data-testid="tab-bundles"><FileArchive className="w-4 h-4 mr-1" /> 510(k) Bundles</TabsTrigger>
              <TabsTrigger value="cdsco" data-testid="tab-cdsco"><FileText className="w-4 h-4 mr-1" /> CDSCO (India)</TabsTrigger>
            </TabsList>
            <TabsContent value="checklist"><ChecklistTab pid={pid} /></TabsContent>
            <TabsContent value="bundles"><BundlesTab pid={pid} /></TabsContent>
            <TabsContent value="cdsco"><CDSCOTab pid={pid} /></TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}

function ChecklistTab({ pid }) {
  const [data, setData] = useState({ sections: [], counts: {}, readiness_pct: 0, total_sections: 0 });
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ status: "not_started", evidence_link: "", notes: "" });

  const load = useCallback(() => {
    api.get(`/submissions/510k/checklist/${pid}`)
      .then(({ data }) => setData(data))
      .catch((e) => logger.warn("Checklist:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const openEditor = (section) => {
    setEdit(section);
    setForm({ status: section.status || "not_started", evidence_link: section.evidence_link || "", notes: section.notes || "" });
  };

  const save = async () => {
    try {
      await api.post("/submissions/510k/checklist", {
        project_id: pid, section_id: edit.section_id, ...form,
      });
      toast.success("Saved.");
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="vq-card p-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
          <div className="font-bold text-2xl text-blue-700" data-testid="sub-readiness-pct">{data.readiness_pct}%</div>
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900">510(k) submission readiness</div>
          <div className="text-xs text-slate-500 mt-1">
            {data.counts.complete || 0} complete · {data.counts.in_progress || 0} in progress · {data.counts.not_started || 0} not started · {data.counts.not_applicable || 0} N/A
            <span className="ml-2">(of {data.total_sections} sections)</span>
          </div>
        </div>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-12">#</th>
              <th className="text-left px-3 py-2">Section</th>
              <th className="text-left px-3 py-2 w-48">Status</th>
              <th className="text-right px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody data-testid="checklist-tbody">
            {data.sections.map((s) => (
              <tr key={s.section_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{s.section_id}</td>
                <td className="px-3 py-2">{s.title}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={
                    s.status === "complete" ? "text-emerald-700 font-semibold" :
                    s.status === "in_progress" ? "text-amber-700 font-semibold" :
                    s.status === "not_applicable" ? "text-slate-500" : "text-slate-500"
                  }>
                    {s.status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" data-testid={`edit-section-${s.section_id}`} onClick={() => openEditor(s)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Section {edit?.section_id} — {edit?.title}</DialogTitle>
            <DialogDescription>Update status and evidence for this 510(k) section.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="section-status"><SelectValue /></SelectTrigger>
                <SelectContent>{CK_STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Evidence link (URL / document ID)</Label>
              <Input data-testid="section-evidence" value={form.evidence_link} onChange={(e) => setForm({ ...form, evidence_link: e.target.value })} />
            </div>
            <div>
              <Label>Reviewer notes</Label>
              <Textarea data-testid="section-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="section-save" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BundlesTab({ pid }) {
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ predicate_name: "", predicate_k_number: "", predicate_intended_use: "" });
  const [busy, setBusy] = useState(false);
  const [signBundle, setSignBundle] = useState(null);
  const [signSection, setSignSection] = useState("");
  const [signPwd, setSignPwd] = useState("");
  const [signNotes, setSignNotes] = useState("");
  const [signing, setSigning] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const load = useCallback(() => {
    api.get("/submissions/510k/bundles", { params: { project_id: pid } })
      .then(({ data }) => setBundles(data))
      .catch((e) => logger.warn("Bundles:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/submissions/510k/generate", { project_id: pid, ...form });
      toast.success(`Draft bundle generated: ${data.bundle_id}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate failed."); }
    finally { setBusy(false); }
  };

  const downloadFile = async (bundle_id, kind) => {
    try {
      // Use `credentials: 'include'` so the HttpOnly session cookie is sent —
      // no more Authorization header from localStorage.
      const res = await fetch(`${API}/submissions/510k/${kind}/${bundle_id}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `510k_draft_${bundle_id}.${kind === "download" ? "zip" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(`Download failed: ${e.message}`); }
  };

  const openSignoff = async (bundle_id) => {
    try {
      const { data } = await api.get(`/submissions/510k/bundles/${bundle_id}`);
      setSignBundle(data);
      const signedIds = new Set((data.signoffs || []).map((s) => s.section_id));
      const first = (data.sections || []).find((s) => !signedIds.has(s.section_id));
      setSignSection(first ? first.section_id : "");
      setSignPwd(""); setSignNotes("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load bundle."); }
  };

  const submitSignoff = async () => {
    if (!signSection) return toast.error("Pick a section to sign.");
    setSigning(true);
    try {
      // Create a one-shot 21 CFR Part 11 e-signature first.
      const { data: sig } = await api.post("/esign", {
        entity_type: "k510_bundle", entity_id: signBundle.bundle_id,
        action: "k510_signoff", meaning: "reviewer_signoff",
        reason: `510(k) Section ${signSection} review sign-off`,
        password: signPwd,
      });
      // Consume the signature on the bundle
      await api.post(`/submissions/510k/bundles/${signBundle.bundle_id}/signoff`, {
        section_id: signSection, signature_id: sig.signature_id, notes: signNotes,
      });
      toast.success(`Section ${signSection} signed off.`);
      // Refresh the bundle in-place
      const { data } = await api.get(`/submissions/510k/bundles/${signBundle.bundle_id}`);
      setSignBundle(data);
      const signedIds = new Set((data.signoffs || []).map((s) => s.section_id));
      const next = (data.sections || []).find((s) => !signedIds.has(s.section_id));
      setSignSection(next ? next.section_id : "");
      setSignPwd(""); setSignNotes("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign-off failed."); }
    finally { setSigning(false); }
  };

  const releaseBundle = async () => {
    if (!signBundle) return;
    if (!window.confirm("Release this bundle? Released bundles become immutable.")) return;
    setReleasing(true);
    try {
      await api.post(`/submissions/510k/bundles/${signBundle.bundle_id}/release`);
      toast.success("Bundle released and locked.");
      const { data } = await api.get(`/submissions/510k/bundles/${signBundle.bundle_id}`);
      setSignBundle(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed."); }
    finally { setReleasing(false); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">{bundles.length} bundle{bundles.length === 1 ? "" : "s"} generated for this project</div>
        <Button data-testid="new-bundle-btn" onClick={() => setOpen(true)}><Sparkles className="w-4 h-4 mr-1" /> Generate bundle</Button>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Bundle ID</th>
              <th className="text-left px-3 py-2">Device</th>
              <th className="text-left px-3 py-2">Generated</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-right px-3 py-2 w-72"></th>
            </tr>
          </thead>
          <tbody data-testid="bundles-tbody">
            {bundles.length === 0 && (
              <tr><td colSpan="5" className="text-center px-3 py-6 text-slate-400">No bundles yet. Click <b>Generate bundle</b>.</td></tr>
            )}
            {bundles.map((b) => (
              <tr key={b.bundle_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{b.bundle_id}</td>
                <td className="px-3 py-2">{b.device_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(b.generated_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">
                  {b.state === "released" ? (
                    <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Released
                    </span>
                  ) : (
                    <span className="text-amber-700">Draft</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  <Button size="sm" variant="outline" data-testid={`signoff-${b.bundle_id}`} onClick={() => openSignoff(b.bundle_id)}>
                    <PenLine className="w-3 h-3 mr-1" /> Sign-off
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`dl-zip-${b.bundle_id}`} onClick={() => downloadFile(b.bundle_id, "download")}>
                    <Download className="w-3 h-3 mr-1" /> ZIP
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`dl-pdf-${b.bundle_id}`} onClick={() => downloadFile(b.bundle_id, "pdf")}>
                    <FileText className="w-3 h-3 mr-1" /> PDF
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="vq-card p-4 border-l-4 border-l-amber-500 bg-amber-50 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900">
          Generated bundles are auto-scaffolded drafts. Every section must be authored, verified, and signed by a
          qualified regulatory reviewer before submission via eSTAR. VitaQMS does <b>not</b> submit to the FDA and
          does <b>not</b> guarantee acceptance or clearance.
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate 510(k) draft bundle</DialogTitle>
            <DialogDescription>Assembles all sections from your device data, risk register, threat model and SBOM.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Predicate device name</Label>
              <Input data-testid="predicate-name" value={form.predicate_name} onChange={(e) => setForm({ ...form, predicate_name: e.target.value })} />
            </div>
            <div>
              <Label>Predicate 510(k) number</Label>
              <Input data-testid="predicate-k" value={form.predicate_k_number} onChange={(e) => setForm({ ...form, predicate_k_number: e.target.value })} placeholder="K123456" />
            </div>
            <div>
              <Label>Predicate intended use</Label>
              <Textarea data-testid="predicate-iu" rows={3} value={form.predicate_intended_use} onChange={(e) => setForm({ ...form, predicate_intended_use: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="generate-bundle-submit" onClick={generate} disabled={busy}>{busy ? "Generating…" : "Generate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signBundle} onOpenChange={(o) => !o && setSignBundle(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reviewer sign-off — {signBundle?.bundle_id}</DialogTitle>
            <DialogDescription>
              21 CFR Part 11 §11.200 e-signatures. Each section requires at least one reviewer sign-off before
              the bundle can be released. Released bundles are immutable.
            </DialogDescription>
          </DialogHeader>
          {signBundle && (
            <div className="space-y-3">
              <div className="vq-card p-3 bg-slate-50">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Sign-off progress</div>
                {(() => {
                  const signed = new Set((signBundle.signoffs || []).map((s) => s.section_id));
                  const total = signBundle.sections?.length || 0;
                  const done = signed.size;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-semibold" data-testid="signoff-progress">{done} / {total}</span>
                          <span className="text-slate-500 ml-1">sections signed</span>
                        </div>
                        {signBundle.state === "released" ? (
                          <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Released {signBundle.released_at && `at ${new Date(signBundle.released_at).toLocaleString()}`}
                          </span>
                        ) : (
                          <Button size="sm" data-testid="release-bundle-btn"
                                  onClick={releaseBundle} disabled={done < total || releasing}>
                            <Lock className="w-3 h-3 mr-1" /> {releasing ? "Releasing…" : "Release bundle"}
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 h-2 bg-slate-200 rounded">
                        <div className="h-full bg-emerald-600 rounded" style={{ width: `${pct}%` }} />
                      </div>
                    </>
                  );
                })()}
              </div>

              {signBundle.state !== "released" && (
                <>
                  <div>
                    <Label>Section to sign</Label>
                    <Select value={signSection} onValueChange={setSignSection}>
                      <SelectTrigger data-testid="signoff-section"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        {(signBundle.sections || [])
                          .filter((s) => !(signBundle.signoffs || []).some((so) => so.section_id === s.section_id))
                          .map((s) => (
                            <SelectItem key={s.section_id} value={s.section_id}>
                              {s.section_id} — {s.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Password (21 CFR Part 11 identity verification)</Label>
                    <Input data-testid="signoff-password" type="password"
                           value={signPwd} onChange={(e) => setSignPwd(e.target.value)}
                           placeholder="Your account password (or 'google-sso-confirm' for Google SSO users)" />
                  </div>
                  <div>
                    <Label>Reviewer notes (optional)</Label>
                    <Textarea data-testid="signoff-notes" rows={3}
                              value={signNotes} onChange={(e) => setSignNotes(e.target.value)} />
                  </div>
                </>
              )}

              {(signBundle.signoffs || []).length > 0 && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Sign-off log</div>
                  <div className="vq-card overflow-hidden">
                    <table className="w-full text-xs">
                      <tbody data-testid="signoff-log">
                        {signBundle.signoffs.map((s) => (
                          <tr key={`${s.section_id}-${s.signed_at}`} className="border-b border-slate-100 last:border-0">
                            <td className="px-3 py-1.5 font-mono">{s.section_id}</td>
                            <td className="px-3 py-1.5">{s.signed_by_name || s.signed_by}</td>
                            <td className="px-3 py-1.5 text-slate-500">{new Date(s.signed_at).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSignBundle(null)}>Close</Button>
            {signBundle && signBundle.state !== "released" && (
              <Button data-testid="signoff-submit" onClick={submitSignoff}
                      disabled={signing || !signSection || signPwd.length < 3}>
                {signing ? "Signing…" : "Sign section"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CDSCOTab({ pid }) {
  const [forms, setForms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/submissions/cdsco", { params: { project_id: pid } })
      .then(({ data }) => setForms(data))
      .catch((e) => logger.warn("CDSCO list:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const generate = async (form_type) => {
    setBusy(true);
    try {
      const { data } = await api.post("/submissions/cdsco/generate", { project_id: pid, form_type });
      toast.success(`${form_type} draft generated.`);
      setDetail(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cdsco-form-cards">
        {[
          { code: "MD-14", desc: "Application for grant of licence to Import Medical Devices (Rule 34)." },
          { code: "MD-15", desc: "Licence to Manufacture Medical Devices for Sale/Distribution (Rule 20)." },
          { code: "MD-42", desc: "Test Licence for Medical Devices (Rule 89)." },
        ].map((f) => (
          <div key={f.code} className="vq-card p-4">
            <div className="font-display font-bold text-slate-900">{f.code}</div>
            <div className="text-xs text-slate-600 mt-1">{f.desc}</div>
            <Button className="mt-3" size="sm" data-testid={`gen-${f.code}`}
                    onClick={() => generate(f.code)} disabled={busy}>
              Generate scaffold
            </Button>
          </div>
        ))}
      </div>

      <div className="vq-card overflow-hidden">
        <div className="p-3 border-b bg-slate-50 font-display font-bold text-sm">Generated drafts</div>
        <table className="w-full text-sm">
          <tbody data-testid="cdsco-tbody">
            {forms.length === 0 && (
              <tr><td className="text-center px-3 py-6 text-slate-400">No CDSCO drafts yet.</td></tr>
            )}
            {forms.map((f) => (
              <tr key={f.form_id} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                  onClick={() => setDetail(f)}>
                <td className="px-3 py-2 font-mono text-xs">{f.form_id}</td>
                <td className="px-3 py-2 font-medium">{f.form_type}</td>
                <td className="px-3 py-2 text-slate-600">{f.template_title}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(f.generated_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.form_type} — {detail?.template_title}</DialogTitle>
            <DialogDescription>{detail?.rule}</DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap text-xs bg-slate-50 border border-slate-200 rounded p-3"
                 data-testid="cdsco-markdown">{detail?.markdown || ""}</pre>
          </div>
          <div className="vq-card p-3 border-l-4 border-l-amber-500 bg-amber-50 text-xs text-amber-900 mt-3">
            {detail?.disclaimer}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

